import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Users, Trophy, CheckSquare, Clock, ArrowRight, Sparkles, Bell, CalendarClock,
  BarChart3, TrendingUp, UserPlus, Activity as ActivityIcon, Video, MessageSquare,
} from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { OnboardingChecklist } from '@/components/dashboard/OnboardingChecklist';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { formatDate, formatDateTime, formatTime } from '@/lib/dateFormat';
import { TrendChart, Donut } from '@/components/dashboard/DashboardCharts';

// ── Types (GET /reports/dashboard-today) ───────────────────────────

interface TodayFollowup {
  id: string;
  nextFollowupAt: string;
  remark: string;
  status: string;
  lead: { id: string; name: string; status: string | null };
  user: { id: string; name: string };
}
interface RecentLead {
  id: string;
  name: string;
  status: string | null;
  source: string | null;
  createdAt: string;
  assignedTo: { id: string; name: string } | null;
}
interface Dashboard {
  todayFollowups: TodayFollowup[];
  recentLeads: RecentLead[];
  trend: { date: string; count: number }[];
  tenantName?: string | null;
  scope?: 'team' | 'own';
  kpis?: {
    totalLeads: number; newThisMonth: number; converted: number; conversionRate: number;
    openTasks: number; overdueTasks: number;
    avgFirstResponseSec: number | null; overSla: number; slaSeconds: number;
  };
  monthlyTrend?: { month: string; count: number }[];
  pipeline?: { name: string; color: string; count: number }[];
  topSources?: { name: string; count: number }[];
  topPerformers?: { id: string; name: string; won: number; leads: number }[];
  tasksDue?: { id: string; title: string; dueAt: string | null; priority: string; lead: { id: string; name: string } | null }[];
  reminders?: { id: string; title: string; dueAt: string; type: string; lead: { id: string; name: string } | null }[];
  meetingsToday?: { id: string; guestName: string; startTime: string; endTime: string; meetingLink: string | null; leadId: string | null; eventName: string | null }[];
  recentActivity?: {
    id: string; actionType: string; payload: any; createdAt: string; actorName: string | null;
    lead: { id: string; name: string; source: string | null } | null;
  }[];
}

// ── Helpers ────────────────────────────────────────────────────────

function timeAgo(iso: string): string {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  const d = Math.floor(s / 86400);
  return d < 7 ? `${d} day${d > 1 ? 's' : ''} ago` : formatDate(iso);
}

function fmtTime(iso: string): string {
  try { return formatTime(iso); }
  catch { return '—'; }
}

function fmt12(hhmm: string): string {
  const [h, m] = hhmm.split(':').map(Number);
  if (Number.isNaN(h)) return hhmm;
  return `${String(h % 12 || 12).padStart(2, '0')}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
}

// 45 sec · 12 min · 3 hrs · 1 day 19 hrs
function fmtDuration(sec: number | null | undefined): string {
  if (sec == null) return '—';
  if (sec < 60) return `${Math.round(sec)} sec`;
  if (sec < 3600) return `${Math.round(sec / 60)} min`;
  if (sec < 86400) return `${Math.round(sec / 3600)} hrs`;
  const d = Math.floor(sec / 86400);
  const h = Math.round((sec % 86400) / 3600);
  return `${d} day${d > 1 ? 's' : ''}${h ? ` ${h} hrs` : ''}`;
}

const nf = (n: number) => n.toLocaleString('en-IN');

function greeting(): string {
  const h = new Date().getHours();
  return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
}

// One-line description of a lead activity for the "Recent activity" feed.
function activityText(a: NonNullable<Dashboard['recentActivity']>[number]): { title: string; detail: string } {
  const p = a.payload ?? {};
  const ch = p.changes?.status ?? p.status;
  switch (a.actionType) {
    case 'lead_created': return { title: 'Lead created', detail: '' };
    case 'lead_status_changed': return { title: 'Status changed', detail: ch?.from || ch?.to ? `${ch?.from ?? '—'} → ${ch?.to ?? '—'}` : '' };
    case 'lead_updated': return { title: 'Lead updated', detail: p.changes ? Object.keys(p.changes).join(', ') : '' };
    case 'meeting_remark': return { title: 'Meeting remark', detail: p.remark ?? '' };
    case 'meeting_status_changed': return { title: 'Meeting updated', detail: [p.to, p.reason].filter(Boolean).join(' — ') };
    case 'meeting_ai_summary': return { title: 'AI meeting summary', detail: String(p.summary ?? '').split('\n')[0] };
    case 'ai_call': return { title: 'AI call', detail: p.summary ?? p.outcome ?? '' };
    default: return {
      title: a.actionType.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase()),
      detail: p.remark ?? p.message ?? p.body ?? p.summary ?? p.text ?? '',
    };
  }
}

// ── Building blocks ────────────────────────────────────────────────

function Card({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <div className={`bg-white rounded-2xl border border-slate-200 shadow-sm ${className}`}>{children}</div>;
}

function CardHead({ icon, title, subtitle, href }: { icon?: React.ReactNode; title: string; subtitle?: string; href?: string }) {
  return (
    <div className="flex items-start justify-between gap-3 mb-4">
      <div className="flex items-start gap-2.5 min-w-0">
        {icon && <div className="h-8 w-8 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0 text-[#6366F1]">{icon}</div>}
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
          {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
        </div>
      </div>
      {href && (
        <Link to={href} className="inline-flex items-center gap-1 text-xs text-[#6366F1] hover:underline flex-shrink-0">
          View all <ArrowRight className="h-3 w-3" />
        </Link>
      )}
    </div>
  );
}

function Empty({ text }: { text: string }) {
  return <p className="py-8 text-center text-sm text-slate-400">{text}</p>;
}

function Skeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: rows }).map((_, i) => <div key={i} className="h-10 bg-slate-50 rounded-lg animate-pulse" />)}
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────────────

export function DashboardPage() {
  const { user } = useAuth();
  const [data, setData] = useState<Dashboard | null>(null);
  const [alerts, setAlerts] = useState<{ type: string; title: string; message: string; severity: string }[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Each call has its own catch so one failure doesn't blank the rest.
    Promise.all([
      api.get('/reports/dashboard-today').then((r) => setData(r.data)).catch(() => {}),
      api.get('/ai/smart-alerts').then((r) => setAlerts(r.data?.alerts ?? [])).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  const k = data?.kpis;
  const followups = data?.todayFollowups ?? [];
  const tasks = data?.tasksDue ?? [];
  const reminders = data?.reminders ?? [];
  const meetings = data?.meetingsToday ?? [];
  const pipeline = data?.pipeline ?? [];
  const pipelineTotal = pipeline.reduce((a, p) => a + p.count, 0);
  const monthly = data?.monthlyTrend ?? [];
  const yearTotal = monthly.reduce((a, m) => a + m.count, 0);
  const sources = data?.topSources ?? [];
  const sourceMax = Math.max(1, ...sources.map((s) => s.count));
  const scopeLabel = data?.scope === 'own' ? 'Your leads' : 'All leads';

  const firstName = (user?.name || '').split(' ')[0] || 'there';
  const todayLabel = useMemo(() => formatDate(new Date().toISOString()), []);

  const kpiCards = [
    {
      label: 'Total leads', value: k ? nf(k.totalLeads) : '—',
      sub: k ? `${nf(k.newThisMonth)} new this month` : '', icon: <Users className="h-4 w-4" />,
      tone: 'bg-indigo-50 text-[#6366F1]', href: '/leads',
    },
    {
      label: 'Deals won', value: k ? nf(k.converted) : '—',
      sub: k ? `${k.conversionRate}% conversion` : '', icon: <Trophy className="h-4 w-4" />,
      tone: 'bg-emerald-50 text-emerald-600', href: '/leads',
    },
    {
      label: 'Open tasks', value: k ? nf(k.openTasks) : '—',
      sub: k ? (k.overdueTasks ? `${nf(k.overdueTasks)} overdue` : 'Nothing overdue') : '', icon: <CheckSquare className="h-4 w-4" />,
      tone: 'bg-amber-50 text-amber-600', href: '/tasks',
    },
    {
      label: 'Avg first response', value: k ? fmtDuration(k.avgFirstResponseSec) : '—',
      sub: k ? `${nf(k.overSla)} over the ${fmtDuration(k.slaSeconds)} SLA` : '', icon: <Clock className="h-4 w-4" />,
      tone: 'bg-violet-50 text-violet-600', href: '/reports',
    },
  ];

  return (
    <DashboardLayout title="Dashboard">
      <div className="space-y-5">
        {/* Greeting + quick links */}
        <Card className="relative overflow-hidden p-5">
          <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-indigo-50" />
          <div className="relative flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-xs text-slate-500">{todayLabel}{data?.tenantName ? ` · ${data.tenantName}` : ''}</p>
              <h2 className="mt-1 text-xl sm:text-2xl font-bold text-slate-900">{greeting()}, {firstName}</h2>
              <p className="mt-1 text-sm text-slate-500">
                You have <b className="text-slate-800">{followups.length}</b> follow-ups,{' '}
                <b className="text-slate-800">{tasks.length}</b> tasks,{' '}
                <b className="text-slate-800">{reminders.length}</b> reminders and{' '}
                <b className="text-slate-800">{meetings.length}</b> meetings today.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {[
                { to: '/leads', label: 'All leads', icon: <Users className="h-4 w-4" /> },
                { to: '/tasks', label: 'Tasks', icon: <CheckSquare className="h-4 w-4" /> },
                { to: '/reminders', label: 'Reminders', icon: <Bell className="h-4 w-4" /> },
                { to: '/reports', label: 'Reports', icon: <BarChart3 className="h-4 w-4" /> },
              ].map((q) => (
                <Link key={q.to} to={q.to} className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 hover:bg-slate-50">
                  {q.icon} {q.label}
                </Link>
              ))}
            </div>
          </div>
        </Card>

        {/* Onboarding — admin-only, silent on failure */}
        {user?.role === 'admin' && (
          <ErrorBoundary silent>
            <OnboardingChecklist />
          </ErrorBoundary>
        )}

        {/* KPI tiles */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {kpiCards.map((c) => (
            <Link key={c.label} to={c.href} className="group bg-white rounded-2xl border border-slate-200 shadow-sm p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <p className="text-sm text-slate-500">{c.label}</p>
                <span className={`h-8 w-8 rounded-lg flex items-center justify-center ${c.tone}`}>{c.icon}</span>
              </div>
              {loading ? (
                <div className="mt-3 h-7 w-24 rounded bg-slate-100 animate-pulse" />
              ) : (
                <p className="mt-2 text-2xl font-bold text-slate-900">{c.value}</p>
              )}
              <p className="mt-1 text-xs text-slate-500">{c.sub}</p>
            </Link>
          ))}
        </div>

        {/* AI smart alerts — only when something is actionable */}
        {alerts.length > 0 && (
          <Card className="p-5">
            <CardHead icon={<Sparkles className="h-4 w-4" />} title="Needs attention today" subtitle="Suggested by AI" />
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
              {alerts.map((a) => {
                const style = a.severity === 'high'
                  ? { box: 'border-red-200 bg-red-50', dot: 'bg-red-500' }
                  : a.severity === 'medium'
                  ? { box: 'border-amber-200 bg-amber-50', dot: 'bg-amber-500' }
                  : { box: 'border-slate-200 bg-slate-50', dot: 'bg-slate-400' };
                return (
                  <Link to="/leads" key={a.type} className={`flex items-start gap-2.5 rounded-lg border p-3 transition hover:shadow-sm ${style.box}`}>
                    <span className={`mt-1.5 h-2 w-2 flex-shrink-0 rounded-full ${style.dot}`} />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-800">{a.title}</p>
                      <p className="text-xs text-slate-500">{a.message}</p>
                    </div>
                  </Link>
                );
              })}
            </div>
          </Card>
        )}

        {/* Lead trend + pipeline */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <Card className="p-5 lg:col-span-2">
            <div className="flex items-start justify-between gap-3 mb-2">
              <div>
                <h3 className="text-sm font-semibold text-slate-900">Lead trend</h3>
                <p className="text-xs text-slate-500">New leads by month · last 12 months · {scopeLabel.toLowerCase()}</p>
              </div>
              <span className="inline-flex items-center gap-1 text-sm font-semibold text-[#6366F1]">
                <TrendingUp className="h-4 w-4" /> {nf(yearTotal)}
              </span>
            </div>
            {loading ? <div className="h-48 rounded-lg bg-slate-50 animate-pulse" /> : monthly.length ? <TrendChart data={monthly} /> : <Empty text="No leads yet" />}
          </Card>

          <Card className="p-5">
            <h3 className="text-sm font-semibold text-slate-900">Pipeline</h3>
            <p className="text-xs text-slate-500 mb-4">Leads by status · {scopeLabel.toLowerCase()}</p>
            {loading ? (
              <div className="h-40 rounded-lg bg-slate-50 animate-pulse" />
            ) : !pipeline.length ? (
              <Empty text="No leads yet" />
            ) : (
              <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-start lg:flex-col xl:flex-row">
                <Donut items={pipeline} total={pipelineTotal} />
                <ul className="w-full min-w-0 space-y-1.5">
                  {pipeline.map((p) => (
                    <li key={p.name} className="flex items-center gap-2 text-xs">
                      <span className="h-2.5 w-2.5 flex-shrink-0 rounded-full" style={{ backgroundColor: p.color }} />
                      <span className="flex-1 truncate text-slate-700" title={p.name}>{p.name}</span>
                      <span className="font-semibold text-slate-900 tabular-nums">{nf(p.count)}</span>
                      <span className="w-9 text-right text-slate-400 tabular-nums">
                        {pipelineTotal ? Math.round((p.count / pipelineTotal) * 100) : 0}%
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Card>
        </div>

        {/* Today: follow-ups, tasks, reminders, meetings */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          <Card className="p-5">
            <CardHead icon={<MessageSquare className="h-4 w-4" />} title="Follow-ups today" subtitle={data?.scope === 'own' ? 'Your follow-ups' : 'Team follow-ups'} href="/leads" />
            {loading ? <Skeleton /> : !followups.length ? <Empty text="No follow-ups today" /> : (
              <ul className="divide-y divide-slate-100">
                {followups.slice(0, 6).map((f) => (
                  <li key={f.id}>
                    <Link to={`/leads/${f.lead.id}`} className="flex items-center gap-3 py-2 hover:bg-slate-50 -mx-2 px-2 rounded-lg">
                      <span className="w-16 text-xs font-medium text-slate-600 flex-shrink-0">{fmtTime(f.nextFollowupAt)}</span>
                      <span className="min-w-0">
                        <span className="block truncate text-sm text-slate-800">{f.lead.name}</span>
                        {f.remark && <span className="block truncate text-xs text-slate-500">{f.remark}</span>}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card className="p-5">
            <CardHead icon={<CheckSquare className="h-4 w-4" />} title="Due today" subtitle="Your open tasks" href="/tasks" />
            {loading ? <Skeleton /> : !tasks.length ? <Empty text="No tasks due today" /> : (
              <ul className="divide-y divide-slate-100">
                {tasks.map((t) => {
                  const overdue = t.dueAt && new Date(t.dueAt).getTime() < new Date().setHours(0, 0, 0, 0);
                  return (
                    <li key={t.id} className="py-2">
                      <p className="truncate text-sm text-slate-800">{t.title}</p>
                      <p className={`text-xs ${overdue ? 'text-red-600' : 'text-slate-500'}`}>
                        {t.dueAt ? (overdue ? `Overdue · ${formatDateTime(t.dueAt)}` : fmtTime(t.dueAt)) : 'No due time'}
                        {t.lead ? ` · ${t.lead.name}` : ''}
                      </p>
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>

          <Card className="p-5">
            <CardHead icon={<Bell className="h-4 w-4" />} title="Reminders" subtitle="Overdue and today" href="/reminders" />
            {loading ? <Skeleton /> : !reminders.length ? <Empty text="No reminders for today" /> : (
              <ul className="divide-y divide-slate-100">
                {reminders.map((r) => {
                  const overdue = new Date(r.dueAt).getTime() < Date.now();
                  return (
                    <li key={r.id} className="py-2">
                      <p className="truncate text-sm text-slate-800">{r.title}</p>
                      <p className={`text-xs ${overdue ? 'text-red-600' : 'text-slate-500'}`}>
                        {overdue ? 'Overdue · ' : ''}{formatDateTime(r.dueAt)}{r.lead ? ` · ${r.lead.name}` : ''}
                      </p>
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>

          <Card className="p-5">
            <CardHead icon={<CalendarClock className="h-4 w-4" />} title="Meetings today" subtitle="Your scheduled bookings" href="/time-availability" />
            {loading ? <Skeleton /> : !meetings.length ? <Empty text="No meetings today" /> : (
              <ul className="divide-y divide-slate-100">
                {meetings.map((m) => (
                  <li key={m.id} className="flex items-center justify-between gap-2 py-2">
                    <div className="min-w-0">
                      {m.leadId ? (
                        <Link to={`/leads/${m.leadId}?tab=meetings`} className="block truncate text-sm text-slate-800 hover:text-[#6366F1]">{m.guestName}</Link>
                      ) : (
                        <p className="truncate text-sm text-slate-800">{m.guestName}</p>
                      )}
                      <p className="text-xs text-slate-500">{fmt12(m.startTime)} – {fmt12(m.endTime)}{m.eventName ? ` · ${m.eventName}` : ''}</p>
                    </div>
                    {m.meetingLink && (
                      <a href={m.meetingLink} target="_blank" rel="noopener noreferrer" className="inline-flex flex-shrink-0 items-center gap-1 rounded-lg bg-blue-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-blue-700">
                        <Video className="h-3.5 w-3.5" /> Join
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>

        {/* Recent leads + top sources */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <Card className="p-5 lg:col-span-2">
            <CardHead icon={<UserPlus className="h-4 w-4" />} title="Recent leads" subtitle="Newest first" href="/leads" />
            {loading ? <Skeleton rows={5} /> : !data?.recentLeads?.length ? <Empty text="No leads yet" /> : (
              <ul className="divide-y divide-slate-100">
                {data.recentLeads.map((l) => (
                  <li key={l.id}>
                    <Link to={`/leads/${l.id}`} className="flex items-center gap-3 py-2.5 -mx-2 px-2 rounded-lg hover:bg-slate-50">
                      <span className="h-8 w-8 rounded-full bg-indigo-50 flex items-center justify-center flex-shrink-0 text-xs font-semibold text-[#6366F1]">
                        {(l.name || '?').charAt(0).toUpperCase()}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm text-slate-800">{l.name}</span>
                        <span className="block truncate text-xs text-slate-500">
                          {l.source || 'Unknown source'}{l.assignedTo ? ` · ${l.assignedTo.name}` : ''}
                        </span>
                      </span>
                      {l.status && (
                        <span className="hidden sm:inline-flex rounded-full border border-indigo-100 bg-indigo-50 px-2 py-0.5 text-xs text-indigo-700 max-w-[160px] truncate">
                          {l.status}
                        </span>
                      )}
                      <span className="w-20 text-right text-xs text-slate-400 flex-shrink-0">{timeAgo(l.createdAt)}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card className="p-5">
            <CardHead title="Top sources" subtitle={scopeLabel} />
            {loading ? <Skeleton /> : !sources.length ? <Empty text="No leads yet" /> : (
              <ul className="space-y-3">
                {sources.map((s) => (
                  <li key={s.name}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="truncate text-slate-700">{s.name}</span>
                      <span className="font-semibold text-slate-900 tabular-nums">{nf(s.count)}</span>
                    </div>
                    <div className="mt-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                      <div className="h-full rounded-full bg-[#6366F1]" style={{ width: `${(s.count / sourceMax) * 100}%` }} />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>

        {/* Recent activity + top performers */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <Card className="p-5 lg:col-span-2">
            <CardHead icon={<ActivityIcon className="h-4 w-4" />} title="Recent activity" subtitle="Across your leads" />
            {loading ? <Skeleton rows={5} /> : !data?.recentActivity?.length ? <Empty text="No activity yet" /> : (
              <ul className="space-y-3">
                {data.recentActivity.map((a) => {
                  const t = activityText(a);
                  return (
                    <li key={a.id} className="flex gap-3">
                      <span className="mt-0.5 h-7 w-7 flex-shrink-0 rounded-full bg-slate-100 flex items-center justify-center text-slate-500">
                        <ActivityIcon className="h-3.5 w-3.5" />
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm text-slate-800">
                          {a.lead ? (
                            <Link to={`/leads/${a.lead.id}`} className="font-medium hover:text-[#6366F1]">{a.lead.name}</Link>
                          ) : 'Lead'}
                          <span className="text-slate-500"> · {t.title}{a.lead?.source ? ` · ${a.lead.source}` : ''}</span>
                        </p>
                        {t.detail && <p className="text-sm text-slate-600 line-clamp-2 break-words">{t.detail}</p>}
                        <p className="text-xs text-slate-400">{a.actorName ?? 'System'} · {timeAgo(a.createdAt)}</p>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>

          <Card className="p-5">
            <CardHead icon={<Trophy className="h-4 w-4" />} title="Top performers" subtitle="Won deals" />
            {loading ? <Skeleton rows={4} /> : !data?.topPerformers?.length ? <Empty text="No won deals yet" /> : (
              <ul className="space-y-3">
                {data.topPerformers.map((p, i) => (
                  <li key={p.id} className="flex items-center gap-3">
                    <span className={`h-7 w-7 flex-shrink-0 rounded-full flex items-center justify-center text-xs font-semibold ${i === 0 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>
                      {i + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm text-slate-800">{p.name}</p>
                      <p className="text-xs text-slate-500">
                        {nf(p.leads)} leads · {p.leads ? ((p.won / p.leads) * 100).toFixed(1) : '0'}% conv.
                      </p>
                    </div>
                    <span className="text-sm font-semibold text-emerald-600 tabular-nums">{nf(p.won)}</span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
